import { useCallback, useEffect, useState } from 'react'
import { Button } from '../../components/ui/Button'
import { Card, CardHeader } from '../../components/ui/Card'
import { SkeletonTable } from '../../components/ui/Skeleton'
import {
  fetchContactMessages,
  setContactMessageStatus,
  type ContactMessageStatus,
  type PlatformContactMessage,
} from '../../services/platformContactApi'

const FILTERS: Array<{ value: '' | ContactMessageStatus; label: string }> = [
  { value: '', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'read', label: 'Read' },
  { value: 'resolved', label: 'Resolved' },
]

const PAGE_SIZE = 20

function formatWhen(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return iso
  }
}

function StatusBadge({ status }: { status: ContactMessageStatus }) {
  const base = 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset'
  if (status === 'new') {
    return <span className={`${base} bg-primary/15 text-primary ring-primary/25`}>New</span>
  }
  if (status === 'resolved') {
    return (
      <span className={`${base} bg-emerald-500/15 text-emerald-800 ring-emerald-500/25 dark:text-emerald-400`}>
        Resolved
      </span>
    )
  }
  return <span className={`${base} bg-muted text-muted-foreground ring-border`}>Read</span>
}

export default function ContactMessagesPage() {
  const [items, setItems] = useState<PlatformContactMessage[]>([])
  const [total, setTotal] = useState(0)
  const [unread, setUnread] = useState(0)
  const [page, setPage] = useState(1)
  const [filter, setFilter] = useState<'' | ContactMessageStatus>('')
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchContactMessages({ page, limit: PAGE_SIZE, status: filter || undefined })
      setItems(data.items)
      setTotal(data.total)
      setUnread(data.unread)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load messages')
    } finally {
      setLoading(false)
    }
  }, [page, filter])

  useEffect(() => {
    void load()
  }, [load])

  async function update(m: PlatformContactMessage, status: ContactMessageStatus) {
    setBusyId(m.id)
    setError(null)
    try {
      await setContactMessageStatus(m.id, status)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update message')
    } finally {
      setBusyId(null)
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Contact messages</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Messages sent from the contact form on the Kartly website.
          {unread > 0 ? ` ${unread} new.` : ''}
        </p>
      </div>

      {error ? (
        <p className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <Card>
        <CardHeader
          title="Inbox"
          description={`${total} message${total === 1 ? '' : 's'}`}
          action={
            <Button type="button" variant="secondary" size="sm" onClick={() => void load()}>
              Refresh
            </Button>
          }
        />

        <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Filter by status">
          {FILTERS.map((f) => (
            <Button
              key={f.value || 'all'}
              type="button"
              size="sm"
              variant={filter === f.value ? 'primary' : 'secondary'}
              onClick={() => {
                setFilter(f.value)
                setPage(1)
              }}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {loading && items.length === 0 ? (
          <SkeletonTable columns={3} rows={4} />
        ) : items.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <p className="text-sm font-medium text-foreground">
              {filter ? 'No messages with this status' : 'No messages yet'}
            </p>
            <p className="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
              New messages from the website contact form will appear here.
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {items.map((m) => (
              <li key={m.id} className="rounded-2xl border border-border bg-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-foreground">{m.senderName}</p>
                    <a
                      href={`mailto:${m.senderEmail}?subject=${encodeURIComponent('Re: your message to Kartly')}`}
                      className="text-sm text-primary hover:underline"
                    >
                      {m.senderEmail}
                    </a>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {m.locale ? <span className="uppercase">{m.locale}</span> : null}
                    <span>{formatWhen(m.createdAt)}</span>
                    <StatusBadge status={m.status} />
                  </div>
                </div>
                <p className="mt-3 whitespace-pre-wrap break-words text-sm text-foreground">{m.body}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {m.status === 'new' ? (
                    <Button type="button" size="sm" variant="secondary" disabled={busyId === m.id} onClick={() => void update(m, 'read')}>
                      Mark as read
                    </Button>
                  ) : null}
                  {m.status !== 'resolved' ? (
                    <Button type="button" size="sm" variant="secondary" disabled={busyId === m.id} onClick={() => void update(m, 'resolved')}>
                      Mark as resolved
                    </Button>
                  ) : (
                    <Button type="button" size="sm" variant="ghost" disabled={busyId === m.id} onClick={() => void update(m, 'read')}>
                      Reopen
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {totalPages > 1 ? (
          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <span>
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button type="button" size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </Button>
              <Button type="button" size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  )
}
